
import { GoogleGenAI } from "@google/genai";
import { Contact, ResearchResult, GroundingSource } from "../types";

// Added comment above fix: Guidelines require creating a new instance right before making an API call
const getAI = () => new GoogleGenAI({ apiKey: process.env.API_KEY });

export const generateEmailDraft = async (contact: Contact, context: string, tone: string = 'Professional') => {
  try {
    const prompt = `
      Draft a ${tone} email to ${contact.contactPerson} at ${contact.companyName}.
      Role: ${contact.role}.
      Context for email: ${context}.
      Keep it concise and action-oriented.
      Return ONLY the email body text, no subject line or headers unless requested.
    `;

    const ai = getAI();
    const response = await ai.models.generateContent({
      model: 'gemini-3-flash-preview',
      contents: prompt,
    });

    return response.text;
  } catch (error) {
    console.error("Error generating email:", error);
    throw error;
  }
};

export const generateWhatsAppDraft = async (contact: Contact, context: string) => {
  try {
    const prompt = `
      Draft a professional but conversational WhatsApp message to ${contact.contactPerson} at ${contact.companyName}.
      Context: ${context}.
      Keep it short (under 50 words), friendly, and direct suitable for instant messaging.
      Return ONLY the message text.
    `;

    const ai = getAI();
    const response = await ai.models.generateContent({
      model: 'gemini-3-flash-preview',
      contents: prompt,
    });

    return response.text;
  } catch (error) {
    console.error("Error generating WhatsApp message:", error);
    throw error;
  }
};

export const researchCompany = async (companyName: string): Promise<ResearchResult> => {
  try {
    const prompt = `Find recent news, key products, and market position for the company "${companyName}". 
    Summarize the findings in 3 concise bullet points. 
    Focus on information useful for a B2B sales meeting.`;

    const ai = getAI();
    const response = await ai.models.generateContent({
      model: 'gemini-3-flash-preview',
      contents: prompt,
      config: {
        tools: [{ googleSearch: {} }],
      }
    });

    const text = response.text || "No information found.";
    
    // Extract grounding sources if available using the correct metadata paths
    const groundingChunks = response.candidates?.[0]?.groundingMetadata?.groundingChunks || [];
    const sources: GroundingSource[] = [];

    groundingChunks.forEach((chunk: any) => {
      if (chunk.web) {
        sources.push({
          title: chunk.web.title || 'Source',
          uri: chunk.web.uri || '#'
        });
      }
    });

    return {
      summary: text,
      sources: sources
    };

  } catch (error) {
    console.error("Error researching company:", error);
    return {
      summary: "Failed to research company. Please check your API key or try again later.",
      sources: []
    };
  }
};
